/**
 * The Mum Hub identity-token recipe, shared by every surface that has to
 * agree on it (MAR-138, MAR-219, MAR-235).
 *
 * There are now three consumers and they run on three runtimes:
 *
 * 1. the iOS/Android app, which hashes with `expo-crypto`;
 * 2. `mobile/scripts/mum-hub-token.mjs`, the CLI that PRINTS the tokens
 *    Adrian pastes into the `mum_hub_allowlist` parameter, which hashes with
 *    `node:crypto`;
 * 3. the website, which hashes with WebCrypto `crypto.subtle`.
 *
 * None of them needs to know how the others compute SHA-256. What they must
 * agree on is the string that goes IN and the truncation that comes OUT, and
 * that is all this module is.
 *
 * WHY THE APP STILL HAS ITS OWN COPY. `mobile/src/lib/feature-flags/
 * mum-hub-audience.mjs` is plain ESM because its two consumers are Metro and
 * bare Node 20, and native ESM is the one format both accept with no build
 * step. This file is the canonical TypeScript statement of the same recipe
 * for the web and anything else that reaches for `@cch/shared`. The two are
 * pinned against each other by
 * `packages/cch-shared/src/__tests__/mum-hub-audience.test.ts`, which imports
 * the app's `.mjs` directly and fails if either side moves.
 *
 * That drift is the whole risk. If the web and the app disagreed about the
 * salt or the normalisation, the tokens in the Firebase console would match
 * on one platform and silently not on the other, and the symptom would look
 * exactly like Firebase being broken: nothing errors, the gate is just shut
 * for ever on one surface.
 */

/**
 * Salt for the identity token. Not a secret — it ships in the app binary and
 * in the web bundle. Its job is to keep the token from being a bare SHA-256
 * of a Gmail address, which would be trivially reversible against any address
 * someone already guessed.
 *
 * It is not access control; the `mum_hub_allowlist` parameter is. And the
 * token is an ENUMERABLE pseudonym, not an opaque one: anyone holding the
 * salt can compute the token for a guessed address and confirm a match. A
 * salted hash of an email under a non-secret salt is still personal data
 * under GDPR and should be treated as such in any privacy review.
 *
 * Changing this value invalidates every token already pasted into the
 * console's array, on every platform at once.
 */
export const MUM_HUB_AUDIENCE_SALT = "cch-mum-hub-audience-v1";

/**
 * 32 hex chars is 128 bits of the digest — far more than enough that two
 * customers will never collide, and short enough to keep the console's
 * allow-list array readable. Every token already in the console is 32 chars,
 * so this is deliberately frozen.
 */
export const MUM_HUB_AUDIENCE_TOKEN_LENGTH = 32;

/** Lower-cased and trimmed, so the same account always hashes identically. */
export function normaliseEmail(email: string | null | undefined): string {
  if (typeof email !== "string") return "";
  return email.trim().toLowerCase();
}

/**
 * The exact string that gets hashed. Every platform hashes THIS, so none of
 * them needs to know how the others compute SHA-256.
 */
export function audienceDigestInput(email: string | null | undefined): string {
  return `${MUM_HUB_AUDIENCE_SALT}:${normaliseEmail(email)}`;
}

/** Truncate a hex digest to the console-configured token length. */
export function audienceTokenFromDigest(digestHex: string): string {
  return digestHex.slice(0, MUM_HUB_AUDIENCE_TOKEN_LENGTH);
}

/** Lower-case hex, the form both `node:crypto` and the CLI already produce. */
function hex(bytes: ArrayBuffer): string {
  return Array.from(new Uint8Array(bytes))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * The whole recipe, for a runtime with WebCrypto: the browser, and Node 20+
 * (which exposes `globalThis.crypto.subtle` too, which is what lets the test
 * suite exercise this exact function rather than a re-implementation).
 *
 * Returns null for an empty or absent address rather than hashing the empty
 * string. A customer with no email on record has nothing to match against and
 * must be left to the global switch alone; hashing `""` would produce a real,
 * stable token that somebody could put in the array by accident and thereby
 * admit EVERY emailless account at once.
 *
 * Returns null rather than throwing when `crypto.subtle` is missing, which on
 * the web means an insecure origin (`crypto.subtle` is undefined outside a
 * secure context). Fail closed: no token can match, so the allow-list arm is
 * simply shut.
 */
export async function mumHubAudienceToken(
  email: string | null | undefined,
): Promise<string | null> {
  const normalised = normaliseEmail(email);
  if (normalised === "") return null;

  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;

  try {
    const digest = await subtle.digest(
      "SHA-256",
      new TextEncoder().encode(audienceDigestInput(normalised)),
    );
    return audienceTokenFromDigest(hex(digest));
  } catch {
    return null;
  }
}
